import React from "react";
import { SidebarSimpleIcon } from "@phosphor-icons/react";

interface SidebarSkeletonProps {
  collapsed?: boolean;
  onExpand?: () => void;
}

// The loading placeholder for the nav (#12209): the notetaker rail's ink
// ground, the toggle in the same top-left spot as the loaded nav's, and pulse
// bars where the products and the history will be.
const bar = "rounded bg-white/10 animate-pulse";

const SidebarSkeleton: React.FC<SidebarSkeletonProps> = ({
  collapsed = false,
  onExpand,
}) => {
  // The rail is 64px wide, so the expanded placeholder's bars overflow it.
  // The toggle here is a real button rather than a placeholder block:
  // without it the user cannot pin the nav until data arrives.
  if (collapsed) {
    return (
      <div
        data-nav-skeleton="rail"
        className="fp-scroll-quiet relative flex h-full select-none flex-col items-center gap-1 overflow-auto bg-[#0e1013] px-2 pb-3 pt-14 text-[#e6e4df]"
      >
        <button
          type="button"
          onClick={onExpand}
          aria-label="Show sidebar"
          className="absolute left-[14px] top-3 grid h-9 w-9 place-items-center rounded-lg text-[#e6e4df]/60 transition-colors hover:bg-white/10 hover:text-[#e6e4df]"
        >
          <SidebarSimpleIcon className="h-5 w-5" />
        </button>
        <ul className="flex w-full flex-col gap-1">
          {[...Array(8)].map((_, i) => (
            <li key={i} className="flex h-9 w-full items-center justify-center">
              <div className={`h-4 w-4 ${bar}`}></div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div
      data-nav-skeleton="panel"
      className="fp-scroll-quiet flex h-full select-none flex-col gap-6 overflow-auto bg-[#0e1013] px-3 py-3 text-[#e6e4df]"
    >
      {/* Header: the toggle's spot, then a wordmark-sized bar beside it */}
      <div className="flex-shrink-0">
        <div className="relative flex h-10 items-center pl-0.5">
          <div className={`h-9 w-9 rounded-lg ${bar}`}></div>
          <div
            className={`absolute left-[58px] h-[28px] w-[86px] ${bar}`}
          ></div>
        </div>

        {/* Product menu items skeleton */}
        <ul className="mt-3 flex flex-col gap-0.5">
          {[...Array(8)].map((_, i) => (
            <li key={i} className="flex h-9 w-full items-center gap-2.5 px-2.5">
              <div className={`h-4 w-4 ${bar}`}></div>
              <div className={`h-4 w-16 ${bar}`}></div>
            </li>
          ))}
        </ul>
      </div>

      {/* Scrollable workspace history area */}
      <div className="ml-2 flex-1">
        {/* Workspace history header */}
        <div className="mb-4">
          <div className={`mb-2 h-4 w-24 ${bar}`}></div>
          <div className={`h-3 w-32 ${bar}`}></div>
        </div>

        {/* Workspace list skeleton */}
        <ul className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <li key={i} className="rounded-lg p-2">
              <div className={`mb-1 h-3 w-3/4 ${bar}`}></div>
              <div className={`h-2 w-1/2 ${bar}`}></div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default SidebarSkeleton;
